import { useEffect, useMemo, useRef, useState } from "react";
import useAuth from "hooks/useAuth";
import useWebotServer from "hooks/useWebotServer";
import useOrgDirectory from "hooks/useOrgDirectory";
import { crmMemberFace } from "../../org/crmMemberFace";
import { formatBudget } from "../../crmFormat";
import { buildAgencySummary, buildHolderViews } from "./agencyPersonModel";
import type { AgencyHolderView, AgencyPool, AgencySummary } from "./agencyPersonModel";
import type { CrmContactHolder } from "../../../../../@types/webotServer";

/**
 * WHO ELSE IN MY AGENCY HOLDS THIS PERSON — one contact, one answer, two hosts.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * It is HOLDERS ONLY. The timeline is not its business any more.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * This used to be `useAgencyPerson`, which also fetched the contact dossier and merged the
 * timeline — because the agency panel drew its own bespoke feed. It does not any more: the
 * panel and the contact page's ציר זמן tab now render the SAME component
 * (`TimelineSurface`), and that component already owns the dossier through the shared
 * `contact-details:{id}` session cache. Two owners for one fetch is how a cache key ends up
 * with two different freshness budgets and re-fires on every tab switch.
 *
 * What that deletes is worth naming: the old hook read the session cache IMPERATIVELY
 * (subscribe, but do not fetch) purely so it could withhold the dossier request from a
 * caller without the agency pool. The surface gets that for free now, because the panel
 * simply does not RENDER it until this hook says the pool is available — see
 * `CrmAgencyPersonPanel`.
 *
 * ── `allowed` is a "do not ask", not an access control ──────────────────────────
 *
 * The server decides. It answers `enabled: false` for a caller without `browse`, for a
 * caller in no agency, and for a multi-agency caller who named none — all three are normal,
 * and all three render identically. `allowed` exists so the request is never ISSUED for the
 * ~95% of the fleet with no agency at all: on the contact page this hook mounts for every
 * contact anybody opens, and the permission answer that decides is already in memory
 * (`useCrmMyAccess`, module-cached and persisted). Not asking beats asking and discarding.
 *
 * ── The seed ────────────────────────────────────────────────────────────────────
 *
 * The CRM list already asked `/contacts/holders` for every id on the page — that is what
 * draws the avatar stacks — so the panel hands that answer straight in and issues nothing.
 * A cold deep link has no seed, and neither does the contact page.
 *
 * ⚠️ AN EMPTY `holders` ARRAY IS NO SEED, whatever the count says. The list's cache stores
 * `count: 1` with no rows for every id the server said nothing about — that is how it knows
 * a person is held by exactly one agent without a payload — so keying "do I have a seed?"
 * on the count would leave a solo-held contact rendering an empty panel forever.
 */

export type ContactAgencyState = {
  /**
   * The agency answered with something to show. FALSE covers a caller in no agency, a
   * member without `browse`, a pool of "ללא", a request that failed, and a caller this hook
   * was told not to ask for. Every one of those renders as "no agency section".
   */
  available: boolean;
  pool: AgencyPool;
  /** Redacted to the pool by `buildHolderViews` — the single enforcement point. */
  holders: AgencyHolderView[];
  /** ⚠️ The TRUE number of copies in scope, INCLUDING the caller's own. */
  count: number;
  loading: boolean;
  /** A genuine fault, as opposed to a refusal. The panel says so out loud. */
  error: boolean;
  summary: AgencySummary;
};

export type ContactAgencySeed = {
  enabled: boolean;
  pool: AgencyPool;
  holders: CrmContactHolder[];
  count: number;
};

const NO_HOLDERS: CrmContactHolder[] = [];

export default function useContactAgency({
  contactId,
  orgId,
  seed = null,
  allowed = true
}: {
  contactId: string | null;
  /** The agency to scope to. `null` lets the server pick, which it can only do for one. */
  orgId: string | null;
  seed?: ContactAgencySeed | null;
  /** The caller has `browse` and a pool above "ללא". Default true — see the header. */
  allowed?: boolean;
}): ContactAgencyState {
  const { user } = useAuth();
  const myUserId = ((user?.email as string | undefined) ?? "").trim().toLowerCase();
  const { contactsHolders } = useWebotServer();
  // Module-cached and shared with every other consumer in the app, so this is a map lookup
  // rather than a request — see `useOrgDirectory`.
  const { getMember } = useOrgDirectory();

  // See the header: rows, not the count, decide whether the seed is usable.
  const seeded = Boolean(seed && seed.holders.length > 0);

  const [fetched, setFetched] = useState<ContactAgencySeed | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  /**
   * ⚠️ THE FETCHER IS HELD IN A REF AND KEPT OUT OF THE EFFECT'S DEPS.
   *
   * `WebotServerContext` builds its value — and every function on it — as a fresh object
   * literal on each of its own renders, and it re-renders whenever the auth context moves.
   * So `contactsHolders` has an unstable identity, and depending on it would re-run this
   * effect (a real HTTP request) on events that have nothing to do with this contact. The
   * CRM list's own holders hook is immune by accident, because it de-dupes on an `asked`
   * ref; this one mounts on the contact page for every contact anybody opens, so it says so
   * out loud instead. Same shape as that hook's `ids`-string dependency.
   */
  const fetcherRef = useRef(contactsHolders);
  useEffect(() => {
    fetcherRef.current = contactsHolders;
  }, [contactsHolders]);

  useEffect(() => {
    if (!contactId || seeded || !allowed) {
      // Drop a previous contact's answer — and its failure — rather than leaving either on
      // screen under a new name. This hook is NOT remounted per contact in the CRM panel,
      // so nothing else clears them.
      setFetched(null);
      setError(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    setError(false);
    fetcherRef
      .current({ orgId: orgId ?? undefined, contactIds: [contactId] })
      .then((response) => {
        if (!live) return;
        setFetched({
          enabled: response.enabled,
          pool: (response.pool ?? "none") as AgencyPool,
          holders: response.holders?.[contactId] ?? NO_HOLDERS,
          count: response.counts?.[contactId] ?? 0
        });
      })
      .catch(() => {
        if (!live) return;
        // ⚠️ RECORDED, unlike the avatar stack's silent failure. A stack that renders
        // nothing costs a decoration; this answer IS the agency section, so a silent
        // failure would be an empty one — which reads as "nobody else has this person",
        // a different and wrong answer. The PANEL shows it; the contact page still just
        // omits the section, because there it is an enrichment.
        setError(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // `contactsHolders` is deliberately absent — see `fetcherRef` above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, orgId, seeded, allowed]);

  const source: ContactAgencySeed | null = seeded ? seed : fetched;
  const pool: AgencyPool = source?.pool ?? "none";

  /**
   * The live face from the org directory, preferred over the holder's own stamp.
   *
   * `ownerName` / `ownerAvatarUrl` on a holder are generated columns over that agent's copy
   * of the contact document — a write-time snapshot, absent on anything written before the
   * stamping Cloud Function existed and stale for anyone who has changed their photo. The
   * seeded path arrives already decorated (`useCrmHolders` does it); the FETCHED path is
   * the contact page and a cold deep link, which is where a colleague would otherwise show
   * up as a bare email beside a colleague who shows up as a photo — on the same list.
   */
  const holders = useMemo(() => {
    const decorated = (source?.holders ?? NO_HOLDERS).map((holder) => {
      const face = crmMemberFace(getMember(holder.ownerUserId), holder.ownerName, holder.ownerAvatarUrl);
      return face.name === holder.ownerName && face.avatarUrl === holder.ownerAvatarUrl
        ? holder
        : { ...holder, ownerName: face.name, ownerAvatarUrl: face.avatarUrl };
    });
    return buildHolderViews(decorated, myUserId, pool, formatBudget);
  }, [source, getMember, myUserId, pool]);

  /**
   * ⚠️ AN ANSWERED-BUT-ABSENT ID MEANS ONE HOLDER, NOT ZERO.
   *
   * The endpoint omits every contact it has nothing extra to say about — that is how the
   * list knows a person is held by exactly one agent without paying for a payload — so a
   * solo-held contact comes back as `counts: {}` and `holders: {}`. Taking that literally
   * printed "שמור אצל 0 מתווכים במשרד" over an empty list, which is not a thing that can be
   * true: the caller is looking at the record, so somebody holds it.
   *
   * Zero survives only for "there is no answer at all" — not allowed, not loaded, refused —
   * which is what every consumer tests with `count > 1` before drawing anything.
   */
  const count = source ? Math.max(source.count ?? 0, holders.length, 1) : 0;

  return {
    available: Boolean(source?.enabled) && pool !== "none",
    pool,
    holders,
    count,
    loading: loading && holders.length === 0,
    error,
    summary: buildAgencySummary(holders, count)
  };
}
